import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { audio } from "../lib/audio";
import { BAND, EN, RAINBOW_ORDER, SECTIONS } from "../lib/sections";
import { clamp01, drawMist, drawRain, drawSky, drawSun, ease, geometry, makeDrops, paintBow, paintClouds, paintLand, streakMask } from "../lib/entranceScene";

// Story beats in ms: rain, the rain easing and clouds parting, sun and rays, glinting drops, the bow forming, the walk under it.
const FULL = { ease: 4000, part: 4000, sun: 5500, rays: 6500, glint: 8000, bow: 8500, smooth: 10000, walk: 12500, end: 17600, rainOff: 6000, band: 950 };
const SIMPLE = { sun: 900, bow: 1700, names: 3000, end: 5600, rainOff: 1200 };
const WORDS_AT = { full: 9000, step: 650, sign: 11800, gather: 16000 };
const WORDS_CALM = { full: 2600, step: 0, sign: 3200, gather: 4700 };
const BLADES = Array.from({ length: 90 }, (_, i) => ({ x: (i * 0.618 + Math.sin(i * 7.3) * 0.02) % 1, y: 0.88 + ((i * 37) % 13) / 100, h: 12 + ((i * 53) % 18), c: ["#7FA36B", "#94B77D", "#6E9460", "#A9C78F"][i % 4], p: i * 1.7 }));
const SPARKS = Array.from({ length: 16 }, (_, i) => ({ a: Math.PI * (0.06 + 0.88 * ((i * 0.618) % 1)), r: 0.86 + ((i * 29) % 30) / 100, p: i * 2.1 }));
const span = (t, a, d) => clamp01((t - a) / d);

function runScene(c, calm, setBand, setNames, finish, release, setStage) {
  const g = c.getContext("2d");
  const dpr = Math.min(1.5, window.devicePixelRatio || 1);
  const T = calm ? SIMPLE : FULL;
  let G, land, bow, streaky, clouds, raf, last = -2, rainOn = true, frames = 0, fpsAt = performance.now(), fps = 0;
  const drops = makeDrops(calm ? 120 : 190);
  const size = () => {
    c.width = c.clientWidth * dpr;
    c.height = c.clientHeight * dpr;
    G = geometry(c.clientWidth, c.clientHeight);
    land = paintLand(G, dpr);
    bow = paintBow(G, dpr);
    streaky = streakMask(bow, dpr);
    clouds = paintClouds(G.W, G.H, dpr);
  };
  size();
  window.addEventListener("resize", size);
  audio.rain?.(true);
  let vt = 0, prev = performance.now();
  const frame = (now) => {
    vt += Math.min(250, Math.max(0, now - prev));
    prev = now;
    frames++;
    if (now - fpsAt >= 1000) (fps = Math.round((frames * 1000) / (now - fpsAt))), (frames = 0), (fpsAt = now);
    const t = window.__afaeFreeze ?? vt;
    const { W, H, hz, cx, cy, R, bw } = G;
    const part = calm ? span(t, T.sun, 1400) : ease(span(t, T.ease, T.part));
    const sunK = calm ? ease(span(t, T.sun, 1300)) : ease(span(t, T.sun, 2500));
    const rays = calm ? 0.6 * sunK : ease(span(t, T.rays, 3500));
    const glint = calm ? 0 : ease(span(t, T.glint, 2000));
    const streakA = calm ? 0 : ease(span(t, T.bow, 3500)) * 0.95;
    const smoothA = calm ? ease(span(t, T.bow, 1500)) * 0.75 : ease(span(t, T.smooth, 2500)) * 0.62;
    const walk = calm ? 0 : span(t, T.walk, T.end - T.walk);
    const s = 1 + 1.8 * walk * walk;
    const amount = calm ? 0.55 * (1 - 0.6 * part) : 1 - 0.55 * part - 0.2 * walk;
    const speed = 1 - 0.45 * part;
    if (rainOn && t > T.rainOff) {
      rainOn = false;
      audio.rain?.(false);
    }
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawSky(g, G, part);
    drawSun(g, G, sunK, rays, t);
    const cw = clouds.left.width / dpr, ch = clouds.left.height / dpr;
    const drift = calm ? 0 : Math.sin(t / 7000) * W * 0.03 + t * 0.004;
    g.globalAlpha = 0.95 * (1 - 0.78 * part);
    g.drawImage(clouds.left, -W * 0.45 - part * W * 0.42 + drift, -H * 0.2 + Math.sin(t / 5200) * 4, cw, ch);
    g.drawImage(clouds.right, W * 0.35 + part * W * 0.42 + drift * 0.7, -H * 0.2 + Math.cos(t / 6100) * 4, cw, ch);
    g.globalAlpha = 1;
    const vy = hz + H * 0.05;
    g.setTransform(dpr * s, 0, 0, dpr * s, dpr * cx * (1 - s), dpr * vy * (1 - s));
    if (streakA > 0) {
      g.globalAlpha = streakA;
      g.drawImage(streaky, 0, 0, W, H);
    }
    if (smoothA > 0) {
      g.globalAlpha = smoothA;
      g.drawImage(bow, 0, 0, W, H);
      if (!calm) {
        // the bands shimmer softly, and a warm glow rises as the rainbow completes
        g.globalCompositeOperation = "lighter";
        g.globalAlpha = smoothA * (0.1 + 0.08 * Math.sin(t / 380));
        g.drawImage(streaky, 0, 0, W, H);
        const glow = ease(span(t, T.smooth + 800, 2200));
        if (glow > 0) {
          const rg = g.createRadialGradient(cx, cy, R * 0.75, cx, cy, R * 1.35);
          rg.addColorStop(0, "rgba(255,240,210,0)");
          rg.addColorStop(0.35, `rgba(255,236,200,${0.2 * glow})`);
          rg.addColorStop(1, "rgba(255,240,210,0)");
          g.globalAlpha = 1;
          g.fillStyle = rg;
          g.fillRect(cx - R * 1.4, cy - R * 1.4, R * 2.8, R * 1.45);
        }
        g.globalCompositeOperation = "source-over";
      }
    }
    g.globalAlpha = 1;
    drawMist(g, G, 0.7 + 0.3 * part);
    g.drawImage(land, 0, 0, W, H);
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.fillStyle = `rgba(150,146,166,${0.32 * (1 - part)})`;
    g.fillRect(0, 0, W, H);
    const scr = { cx, cy: vy + (cy - vy) * s, R: R * s, bw: bw * s };
    drawRain(g, G, drops, { amount, speed, glint, bow: scr, move: !calm });
    if (!calm) {
      // light catching the last drops along the bow: tiny four-point glints, never round
      const k = glint * (1 - walk);
      if (k > 0.02) {
        g.strokeStyle = "rgba(255,252,240,0.95)";
        g.lineWidth = 1.1;
        for (const sp of SPARKS) {
          const tw = Math.max(0, Math.sin(t / 520 + sp.p));
          if (tw < 0.25) continue;
          const x = scr.cx + Math.cos(sp.a) * scr.R * sp.r, y = scr.cy - Math.sin(sp.a) * scr.R * sp.r, l = 3 + 4 * tw;
          g.globalAlpha = k * tw;
          g.beginPath();
          g.moveTo(x - l, y);
          g.lineTo(x + l, y);
          g.moveTo(x, y - l);
          g.lineTo(x, y + l);
          g.stroke();
        }
      }
      // the meadow sways
      g.globalAlpha = 0.85;
      g.lineCap = "round";
      for (const b of BLADES) {
        const x = b.x * W, y = b.y * H, sway = Math.sin(t / 900 + b.p) * 4 + Math.sin(t / 2300 + b.x * 6) * 3;
        g.strokeStyle = b.c;
        g.lineWidth = 1.6;
        g.beginPath();
        g.moveTo(x, y);
        g.quadraticCurveTo(x + sway * 0.4, y - b.h * 0.6, x + sway, y - b.h);
        g.stroke();
      }
      g.globalAlpha = 1;
    }
    const W8 = calm ? WORDS_CALM : WORDS_AT;
    const stage = t >= W8.gather ? 3 : t >= W8.sign ? 2 : t >= W8.full ? 1 : 0;
    if (stage !== runScene.stage) {
      runScene.stage = stage;
      setStage?.({ stage, shown: 0, G: { cx: cx / W, top: (cy - R * 0.62) / H } });
    }
    if (stage >= 1) {
      const shown = W8.step ? Math.min(5, 1 + Math.floor((t - W8.full) / W8.step)) : 5;
      if (shown !== runScene.shown) {
        runScene.shown = shown;
        setStage?.((s) => ({ ...s, shown }));
      }
    }
    const idx = calm ? -1 : t < T.walk + 100 ? -1 : Math.min(4, Math.floor((t - T.walk - 100) / T.band));
    if (idx !== last) {
      last = idx;
      setBand(idx);
    }
    if (calm && t > T.names) setNames(true);
    window.__afaeRainbow = { ms: Math.round(t), total: T.end, band: idx, rain: +amount.toFixed(2), sun: +sunK.toFixed(2), form: +Math.max(streakA, smoothA).toFixed(2), walk: +walk.toFixed(2), fps, mode: calm ? "simple" : "full" };
    if (t >= T.end) {
      release?.();
      return finish();
    }
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
  return () => {
    runScene.stage = runScene.shown = undefined;
    cancelAnimationFrame(raf);
    window.removeEventListener("resize", size);
    audio.rain?.(false);
  };
}

// The entrance story: soft grey rain, the sun breaking through, a rainbow forming in the drops and grounded on the hills, then the path under it.
export function RainbowArrival({ simple, gentle = false, paintings, onDone, onRelease }) {
  const ref = useRef(null);
  const [band, setBand] = useState(-1);
  const [out, setOut] = useState(false);
  const [scene, setScene] = useState({ stage: 0, shown: 0, G: null });
  // The mode is fixed at the start so a frame-rate dip can never restart the story.
  const [calm] = useState(() => simple || gentle);
  const doneRef = useRef(false);
  const done = (why) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone(why);
  };
  useEffect(() => runScene(ref.current, calm, setBand, () => {}, () => setOut(true), onRelease, setScene), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const id = setTimeout(() => setOut(true), (calm ? SIMPLE.end : FULL.end) + 30000);
    return () => clearTimeout(id);
  }, [calm]);
  useEffect(() => {
    if (!out) return;
    const id = setTimeout(() => done("faded"), 1500);
    return () => clearTimeout(id);
  }, [out]); // eslint-disable-line react-hooks/exhaustive-deps
  const zone = band >= 0 ? RAINBOW_ORDER[band] : null;
  return (
    <motion.div data-testid="rainbow-arrival" data-mode={calm ? "simple" : "full"} data-gentle={gentle ? "1" : "0"} data-band={zone || ""} data-stage={scene.stage} className="fixed inset-0 z-[61] overflow-hidden" animate={{ opacity: out ? 0 : 1 }} transition={{ duration: 0.8 }} onAnimationComplete={() => out && done("faded")}>
      <canvas ref={ref} className="h-full w-full" />
      <ArrivalWords paintings={paintings} scene={scene} calm={calm} />
      <AnimatePresence>{scene.stage >= 2 && scene.G && <Sign key="sign" G={scene.G} />}</AnimatePresence>
      <button data-testid="arrival-skip-button" onClick={() => done("skip")} className="absolute bottom-6 right-6 rounded-full border border-white/70 bg-[#FBF7F2]/92 px-5 py-1.5 font-display text-lg text-[#4A2D55] backdrop-blur-md transition-colors duration-200 hover:bg-white">
        Skip
      </button>
    </motion.div>
  );
}

function Sign({ G }) {
  return (
    <motion.div data-testid="rainbow-sign" initial={{ opacity: 0, y: 10, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} exit={{ opacity: 0 }} transition={{ duration: 1.6, ease: "easeOut" }} className="pointer-events-none absolute flex flex-col items-center text-center" style={{ left: `${G.cx * 100}%`, top: `${G.top * 100}%`, x: "-50%" }}>
      <motion.span data-testid="rainbow-sign-welcome" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 0.9, y: 0 }} transition={{ duration: 2, delay: 0.5, ease: "easeOut" }} className="mb-0.5 whitespace-nowrap font-hand text-lg text-[#6B5A80] [text-shadow:0_0_12px_rgba(255,246,230,0.95)] sm:text-2xl">
        Welcome to
      </motion.span>
      <span className="whitespace-nowrap font-display text-[1.75rem] italic leading-tight text-[#4A2D55] [text-shadow:0_0_18px_rgba(255,246,230,0.95)] sm:text-5xl">Rainbow by the Rain</span>
      <span className="mt-1 whitespace-nowrap text-[10px] uppercase tracking-[0.3em] text-[#6B5A80] [text-shadow:0_0_10px_rgba(255,246,230,0.95)] sm:text-xs sm:tracking-[0.45em]">Virtual experience gallery</span>
    </motion.div>
  );
}

// Fixed, non-overlapping places in the open sky (rooms) and the meadow (feeling words), away from the bow.
const ROOM_SLOTS = { tall: [[22, 9], [63, 6], [26, 21], [40, 33], [75, 31]], wide: [[9, 13], [16, 31], [9, 49], [90, 30], [87, 50]] };
const WORD_SLOTS = { tall: [[18, 71], [72, 73], [44, 81], [18, 90], [62, 89]], wide: [[14, 73], [33, 85], [64, 82], [85, 72], [50, 93]] };
// Gather: feeling words drift into a spaced column along the path; room words start toward the path and fade out early,
// well before they could meet each other or the sign.
const ROOM_GATHER = [[38, 88], [44, 90], [50, 92], [56, 90], [62, 88]];
const WORD_GATHER = { tall: [[50, 66], [50, 72], [50, 78], [50, 84], [50, 90]], wide: [[50, 68], [50, 74], [50, 80], [50, 86], [50, 92]] };

function ArrivalWords({ paintings, scene, calm }) {
  const tall = window.innerWidth < window.innerHeight ? "tall" : "wide";
  const feelings = useMemo(() => [...new Set(paintings.map((p) => p.feeling_word).filter(Boolean))].slice(0, 5), [paintings]);
  const gather = scene.stage >= 3;
  const place = ([x, y]) => ({ left: `${x}%`, top: `${y}%` });
  const gatherTo = ([x, y], i, room) => ({
    left: `${x}%`,
    top: `${y}%`,
    opacity: 0,
    scale: room ? 0.85 : 0.8,
    y: 0,
    transition: room
      ? { left: { duration: 1.6, ease: "easeIn" }, top: { duration: 1.6, ease: "easeIn" }, opacity: { duration: 0.6, ease: "easeOut" }, scale: { duration: 1.6 } }
      : { left: { duration: 1.7, ease: "easeInOut", delay: (i - 5) * 0.08 }, top: { duration: 1.7, ease: "easeInOut", delay: (i - 5) * 0.08 }, opacity: { duration: 1.5, ease: "easeIn", delay: (i - 5) * 0.08 }, scale: { duration: 1.7 } },
  });
  const move = (slot, i, on, target) => (gather ? gatherTo(target, i, i < 5) : { ...place(slot), opacity: on ? 1 : 0, scale: 1, y: on && !calm ? [0, -6, 0] : 0, transition: { opacity: { duration: 1.2 }, y: { duration: 6 + i, repeat: Infinity, ease: "easeInOut" } } });
  return (
    <div data-testid="rainbow-words" className="pointer-events-none absolute inset-0">
      {RAINBOW_ORDER.map((z, i) => (
        <motion.div key={z} data-testid={`rainbow-room-word-${z}`} className="absolute" initial={{ ...place(ROOM_SLOTS[tall][i]), opacity: 0 }} animate={move(ROOM_SLOTS[tall][i], i, scene.stage >= 1 && i < scene.shown, ROOM_GATHER[i])}>
          <div className="flex -translate-x-1/2 -translate-y-1/2 flex-col items-center">
          <span className="whitespace-nowrap font-display text-2xl italic sm:text-3xl [text-shadow:0_0_14px_rgba(255,246,230,0.95)]" style={{ color: BAND[z] }}>{SECTIONS[z].name}</span>
          <span className="whitespace-nowrap font-display text-base italic text-[#3A2350] sm:text-lg [text-shadow:0_0_10px_rgba(255,246,230,0.95)]">{EN[z]}</span>
          </div>
        </motion.div>
      ))}
      {feelings.map((w, i) => (
        <motion.div key={w} data-testid="rainbow-word" className="absolute" initial={{ ...place(WORD_SLOTS[tall][i]), opacity: 0 }} animate={move(WORD_SLOTS[tall][i], i + 5, scene.stage >= 1 && i < scene.shown + 1, WORD_GATHER[tall][i])}>
          <span className="block -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-hand text-xl text-[#5B4470] [text-shadow:0_0_12px_rgba(255,244,226,0.95)] sm:text-2xl">{w}</span>
        </motion.div>
      ))}
    </div>
  );
}
